import { EVIDENCE_REVIEW, type EvidenceSearch, type LocalDate } from '@cadencia/contracts';
import { daysBetweenLocal } from '../units/local-date.js';

/**
 * Cuándo la base de evidencia está vencida, y qué se hace entonces (§8).
 *
 * LA REGLA QUE MANDA SOBRE TODAS: **una revisión vencida NO bloquea el
 * entrenamiento.** El §8 lo dice literalmente —«si una actualización falla,
 * utiliza la última versión válida y comunica la limitación sin bloquear el
 * entrenamiento»— y es lo correcto: la alternativa es una aplicación que deja
 * de programar porque no pudo leer un buscador, que es un fallo mucho peor que
 * programar con la evidencia del mes pasado.
 *
 * Por eso aquí no hay ninguna función que devuelva «no se puede». Devuelven
 * QUÉ PASA y CUÁNTO HACE, y quien programa decide seguir diciéndolo.
 *
 * EL INTERVALO ES UNA DECISIÓN DE PRODUCTO, y el §8 insiste en que se declare
 * así: «ese intervalo es una decisión de producto, no un umbral científico». La
 * literatura no cambia el día 30. Lo que cambia el día 30 es cuánto está uno
 * dispuesto a ir por detrás sin mirar.
 */

export type FreshnessState =
  /** Nunca se ha revisado nada. No es lo mismo que estar vencida. */
  | 'never_reviewed'
  | 'fresh'
  /** Dentro del margen de aviso: conviene revisar, todavía no urge. */
  | 'due_soon'
  | 'overdue';

export interface Freshness {
  state: FreshnessState;
  /** Días desde la última búsqueda. `null` si no ha habido ninguna. */
  daysSinceReview: number | null;
  /** La fecha de la última revisión, para poder enseñarla. */
  lastReviewedOn: LocalDate | null;
  /**
   * SIEMPRE `false`. Está aquí para que quien lo lea no tenga que acordarse de
   * que la evidencia vencida no para nada; el §8 lo exige y un campo lo dice
   * mejor que un comentario en otro fichero.
   */
  blocksTraining: false;
}

/**
 * Estado de frescura a partir de las búsquedas hechas.
 *
 * Se mira la búsqueda MÁS RECIENTE, no la ficha más reciente, y la diferencia
 * importa: una ficha puede actualizarse al corregir una errata sin que nadie
 * haya vuelto a buscar nada. Lo que vence es el acto de MIRAR, no el contenido.
 */
export function evidenceFreshness(
  searches: readonly EvidenceSearch[],
  hoy: LocalDate,
  intervalDays: number = EVIDENCE_REVIEW.intervalDays,
): Freshness {
  const vivas = searches.filter((fila) => fila.deletedAt === null);
  if (vivas.length === 0) {
    return {
      state: 'never_reviewed',
      daysSinceReview: null,
      lastReviewedOn: null,
      blocksTraining: false,
    };
  }

  const ultima = vivas.reduce((mayor, fila) =>
    fila.searchedOn > mayor.searchedOn ? fila : mayor,
  );
  const dias = daysBetweenLocal(ultima.searchedOn, hoy);

  const state: FreshnessState =
    dias >= intervalDays
      ? 'overdue'
      : dias >= intervalDays - EVIDENCE_REVIEW.warnBeforeDays
        ? 'due_soon'
        : 'fresh';

  return {
    state,
    daysSinceReview: dias,
    lastReviewedOn: ultima.searchedOn,
    blocksTraining: false,
  };
}

/**
 * Si conviene lanzar una revisión antes de programar un bloque.
 *
 * El §8 propone «una comprobación antes de un nuevo bloque si la revisión está
 * vencida», y esto es esa comprobación. Devuelve `true` también cuando no se ha
 * revisado nunca: la primera vez es justo cuando más falta hace.
 *
 * Quien llama NO debe esperar a que termine para programar. Es una oportunidad,
 * no una precondición.
 */
export function shouldReviewBeforeBlock(freshness: Freshness): boolean {
  return freshness.state === 'overdue' || freshness.state === 'never_reviewed';
}

/**
 * Cómo se dice el estado de la evidencia junto a una programación.
 *
 * Vive en el dominio y no en la pantalla porque el §14 exige que cada decisión
 * diga «qué incertidumbre existe», y esa frase forma parte de la decisión, no
 * de su presentación. Dos pantallas que lo redactaran por su cuenta acabarían
 * diciendo cosas distintas del mismo estado.
 */
export function evidenceCaveat(freshness: Freshness): string | null {
  switch (freshness.state) {
    case 'never_reviewed':
      return 'Todavía no se ha revisado literatura: este plan sale solo de las reglas del motor.';
    case 'overdue':
      return freshness.daysSinceReview === null
        ? null
        : `La evidencia se revisó hace ${String(freshness.daysSinceReview)} días y toca actualizarla. El plan usa la última versión válida.`;
    case 'due_soon':
    case 'fresh':
      return null;
  }
}
